import { ReactElement } from 'react';
import { Field, useFormikContext, getIn } from 'formik';
import { INVALID_NUMERIC_FORMAT } from '@services/ErrorMessages';
import { AddressField, ContactInputWithExperian, EmailFieldWithExperian, TextInput, TileRadioAccessible } from '@components';
import { iContactInfoDisplay, tile } from '@interfaces/interfaces';
import { iInfringementExtensionFormValues } from '@services/InfringementExtensionService';
import { FormType } from '@constants/forms';

const { REACT_APP_SITECORE_API_ENDPOINT: API_URL } = process.env;

interface iContactInformation {
  displayObject: iContactInfoDisplay;
  currentStep: string;
}

function CompanyInformationInfringementExtension({ displayObject, currentStep }: iContactInformation): ReactElement {
  const { values, touched }: { values: iInfringementExtensionFormValues; touched: any } = useFormikContext();
  const formikContext = useFormikContext();
  const Abn = (): ReactElement => (
    <>
      <div>ABN</div>
    </>
  );

  const Acn = (): ReactElement => (
    <>
      <div>ACN</div>
    </>
  );

  const ABNACN_TILE_DATA: tile[] = [
    {
      body: <Abn />,
      value: 'ABN',
    },
    {
      body: <Acn />,
      value: 'ACN',
    },
  ];

  const PostalAddress = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NotPostalAddress = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const POSTAL_TILE_DATA: tile[] = [
    {
      body: <PostalAddress />,
      value: 'true',
    },
    {
      body: <NotPostalAddress />,
      value: 'false',
    },
  ];

  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];

  const validateAbnAsync = (value: string) => {
    const url = `${API_URL}sitecore/PartialForms/ABNLookUp`;
    const payload = {
      InputNumber: value,
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.text())
      .then((responseBody) => {
        if (responseBody === 'False') {
          return 'Please enter a valid ABN';
        }
        return '';
      })
      .catch((error) => error);
  };

  const validateAcnAsync = (value: string) => {
    const url = `${API_URL}sitecore/PartialForms/ACNLookUp`;
    const payload = {
      InputNumber: value,
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.text())
      .then((responseBody) => {
        if (responseBody === 'False') {
          return 'Please enter a valid ACN';
        }
        return '';
      })
      .catch((error) => error);
  };
  if (values.NoEmail) {
    values.EMAIL_TO = 'false';
  }
  const { givenName, familyName, companyPosition, contactNumber, noEmailAddress, emailAddress, emailTo, receiveDocs, companyName, ABNACNType, ABNNo, ACNNo, companyAddress, isAddressSameAsPostal, postalAddress } = displayObject.fields;
  return (
    <>
      <div className="vfH2">Details</div>
      <Field component={TextInput} name={givenName.formikName} title={givenName.title!} isMandatory />
      <Field component={TextInput} name={familyName.formikName} title={familyName.title!} isMandatory />
      <Field component={TextInput} name={companyPosition.formikName} title={companyPosition.title!} isMandatory />
      <Field component={ContactInputWithExperian} name={contactNumber.formikName} title="Contact phone number" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={FormType.INFRINGEMENTEXTENSION} numberTypeMandatory={false} secondaryContact={false} hasPrimaryNumberType={false} warningMessage="CompanyContactNumberWarning" />
      <Field component={EmailFieldWithExperian} name={emailAddress.formikName} title="Email" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={FormType.INFRINGEMENTEXTENSION} noEmailField={noEmailAddress.formikName} currentSP={currentStep} warningMessage="CompanyEmailWarning" />
      <Field
        name={receiveDocs.formikName}
        component={TileRadioAccessible}
        tileData={TILE_DATA}
        title={receiveDocs.title!}
        isMandatory
        orientationRow
      />
      {!values[noEmailAddress.formikName] && (
        <Field
          name={emailTo.formikName}
          component={TileRadioAccessible}
          tileData={TILE_DATA}
          title={emailTo.title!}
          isMandatory
          orientationRow
        />
      )}
      <div>
        <span className="vfH3">Company details</span>
        <Field component={TextInput} name={companyName.formikName} title={companyName.title!} isMandatory />
        <Field
          component={TileRadioAccessible}
          name={ABNACNType.formikName}
          title={ABNACNType.title!}
          tileData={ABNACN_TILE_DATA}
          orientationRow
          isMandatory
          tileChanged={() => {
            formikContext.setFieldValue(ABNNo.formikName, '');
            formikContext.setFieldTouched(ABNNo.formikName, false);
            formikContext.setFieldError(ABNNo.formikName, INVALID_NUMERIC_FORMAT);
            formikContext.setFieldValue(ACNNo.formikName, '');
            formikContext.setFieldTouched(ACNNo.formikName, false);
            formikContext.setFieldError(ACNNo.formikName, INVALID_NUMERIC_FORMAT);
          }}
        />
        {getIn(values, ABNACNType.formikName) === 'ABN' && <Field component={TextInput} name={ABNNo.formikName} title={ABNNo.title!} isMandatory validate={values.AbnNo && touched.AbnNo ? validateAbnAsync : null} />}
        {getIn(values, ABNACNType.formikName) === 'ACN' && <Field component={TextInput} name={ACNNo.formikName} title={ACNNo.title!} isMandatory validate={values.AcnNo && touched.AcnNo ? validateAcnAsync : null} />}
        <AddressField name={companyAddress.formikName} title={companyAddress.title!} manualAddressText="Enter the address manually" isMandatory />
        <Field
          name={isAddressSameAsPostal.formikName}
          component={TileRadioAccessible}
          tileData={POSTAL_TILE_DATA}
          orientationRow
          mobileCollapseRow={false}
          title={isAddressSameAsPostal.title!}
          isMandatory
        />
        {getIn(values, isAddressSameAsPostal.formikName) === 'true' && (
          <AddressField name={postalAddress.formikName} title={postalAddress.title!} manualAddressText="Enter the address manually" isMandatory />
        )}
      </div>
    </>
  );
}

export default CompanyInformationInfringementExtension;
